import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Upload } from 'lucide-react';

import { apiClient } from '../lib/api.js';
import { useAuth } from '../lib/auth-context.js';
import type { TalentDetail } from '@arx-marketplace/shared/talents';
import { cn } from '../lib/utils.js';

export function PublishDetailPage(): JSX.Element {
  const { slug = '' } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState<TalentDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    apiClient
      .getTalent(slug)
      .then(setData)
      .catch((e: Error) => setErr(e.message));
  }, [slug]);

  if (err) return <div className="container py-12 text-destructive">{err}</div>;
  if (!data) return <div className="container py-12 text-muted-foreground">Loading…</div>;
  if (!user || data.owner_id !== user.id) {
    return <div className="container py-12 text-destructive">Not your talent.</div>;
  }

  async function save(): Promise<void> {
    if (!data) return;
    setSaving(true);
    try {
      await apiClient.updateTalent(data.slug, {
        name: data.name,
        summary: data.summary,
        description: data.description,
      });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function uploadVersion(file: File): Promise<void> {
    setUploading(true);
    setErr(null);
    try {
      await apiClient.uploadVersion(slug, file);
      const fresh = await apiClient.getTalent(slug);
      setData(fresh);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setUploading(false);
    }
  }

  return (
    <section className="container py-10 flex-1 max-w-3xl space-y-10">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Edit {data.slug}</h1>
        <p className="text-sm text-muted-foreground mt-1">Metadata, description, and showcase.</p>
      </div>

      <div className="space-y-3">
        <Field label="Name">
          <input
            value={data.name}
            onChange={(e) => setData({ ...data, name: e.target.value })}
            className="w-full px-3 py-2 rounded-md border bg-background"
          />
        </Field>
        <Field label="Summary">
          <input
            value={data.summary}
            onChange={(e) => setData({ ...data, summary: e.target.value })}
            className="w-full px-3 py-2 rounded-md border bg-background"
          />
        </Field>
        <Field label="Description (markdown)">
          <textarea
            value={data.description}
            onChange={(e) => setData({ ...data, description: e.target.value })}
            rows={10}
            className="w-full px-3 py-2 rounded-md border bg-background font-mono text-sm"
          />
        </Field>
        <button
          onClick={() => void save()}
          disabled={saving}
          className={cn(
            'px-3 py-2 rounded-md bg-primary text-primary-foreground',
            'disabled:opacity-50 hover:bg-primary/90 transition-colors',
          )}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      <div className="rounded-lg border p-4">
        <h2 className="font-semibold">Upload a new version</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Pick a <code>.tulpa</code> bundle. Slug, category, and version are read from its
          manifest.
        </p>
        <input
          ref={fileRef}
          type="file"
          accept=".tulpa,.zip,application/zip"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void uploadVersion(f);
          }}
          className="hidden"
        />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className={cn(
            'mt-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-md border',
            'hover:bg-secondary disabled:opacity-50 transition-colors',
          )}
        >
          <Upload className="h-4 w-4" />
          {uploading ? 'Uploading…' : 'Choose .tulpa'}
        </button>
        {data.latest_version && (
          <div className="mt-3 text-sm text-muted-foreground">
            Latest: <code>v{data.latest_version}</code>
          </div>
        )}
      </div>

      <div>
        <h2 className="font-semibold">Versions</h2>
        <ul className="mt-3 divide-y rounded-md border">
          {data.versions.map((v) => (
            <li key={v.id} className="p-3 text-sm flex items-center justify-between">
              <div>
                <code className="font-semibold">v{v.version}</code>
                <span className="ml-3 text-muted-foreground">
                  {(v.size_bytes / 1024).toFixed(1)} KB · sha256 {v.sha256.slice(0, 12)}…
                </span>
              </div>
              <a
                href={apiClient.downloadUrl(data.slug, v.version)}
                className="underline-offset-4 hover:underline"
              >
                download
              </a>
            </li>
          ))}
          {data.versions.length === 0 && (
            <li className="p-3 text-sm text-muted-foreground">No versions uploaded yet.</li>
          )}
        </ul>
      </div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block text-sm">
      <span className="text-muted-foreground">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
